import {
  Button,
  ChevronAndTitle,
  LoadingSpinner,
  WarrantyInfo,
  useTenantBrandIdentity,
} from '@rabbit/elements/shared-components';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import ROUTE_NAME from '../../../utils/url-constants';
import placeholder from '../../../assets/images/no_products_yet.svg';
import { DTVendable } from '@rabbit/data/types';
import { getWarrantyInfo } from '@rabbit/olive/utils/helpers';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { AppContext } from '@rabbit/app-context';
import {
  Config_WarrantyContentSource,
  Config_WarrantyMergeStrategy,
} from '@rabbit/config/enums';
import { resolveWarrantyContent } from '@rabbit/bizproc/react';
import { subscribeDocument } from '@rabbit/utils/api';
import { BL_Warranty } from '@rabbit/bizproc/core';
import AuthStart from '@rabbit/olive/templates/Auth/AuthStart';

export interface VendableInfoContainerProps {
  docid: string;
  onChangeStep?: (step_in: number, data?: any) => void;
  holdingId?: string | null;
  landing?: boolean;
  isSRVHolding?: boolean;
}

export interface ItemProps {
  id?: string;
  title: string;
  description?: string;
  included?: string;
}

export interface InfoInterface {
  title: string;
  type: string;
  items?: ItemProps[];
  children?: any;
}

export function VendableInfoContainer({
  docid,
  onChangeStep,
  landing = false,
  isSRVHolding = false,
}: VendableInfoContainerProps) {
  const { config } = useContext(AppContext);
  const [showWarrantyDetails, setShowWarrantyDetails] = useState(false);
  const [product, setProduct] = useState<DTVendable | undefined>(undefined);
  const [warrantyInfo, setWarrantyInfo] = useState<InfoInterface[]>([]);
  const [warrantyTerms, setWarrantyTerms] = useState<InfoInterface[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tenantInfo } = useContext(AppContext);
  const warrantyDetailsRef = useRef<HTMLDivElement | null>(null);

  const searchParams = new URLSearchParams(window.location.search);
  const params = useParams();
  const productPlaceHolder = tenantInfo?.productPlaceholder || placeholder;
  const [loading, setLoading] = useState(true);
  const { tenantBrandLogo, tenantBrandName } = useTenantBrandIdentity({
    tenantInfo,
    brand: product?.brand,
  });
  const tenantLink = t('tenantLink');

  useEffect(() => {
    let unSub = () => {};
    try {
      setLoading(true);
      const localStorageVendableId =
        localStorage.getItem('vendableId') || undefined;
      const resolvedId = docid ?? localStorageVendableId;
      unSub = subscribeDocument<DTVendable>(
        `vendable/${resolvedId}`,
        (data) => {
          const prod = data.data();
          setProduct(prod);
          setLoading(false);
        }
      );
    } catch (e) {
      setLoading(false);
    }

    return () => {
      unSub();
    };
  }, [docid]);

  useEffect(() => {
    if (window) window.localStorage.removeItem('LRP_VendableId');
    const getWarranty = async () => {
      if (!product) return;
      // Get warranty info and terms from the tenant base, passing brand for brand-specific terms
      // Only pass brand if it's a valid non-empty string
      const brand =
        product?.brand &&
        typeof product.brand === 'string' &&
        product.brand.trim() !== ''
          ? product.brand
          : undefined;
      const tenantDefaultWarrantyContent = await getWarrantyInfo(
        tenantLink,
        brand
      );

      const vendableInfoOverride = ((product as any)?.warrantyInfoOverride ||
        []) as InfoInterface[];
      const vendableTermsOverride = ((product as any)?.warrantyTermsOverride ||
        []) as InfoInterface[];

      const fetchTemplateContent = async (): Promise<{
        info: InfoInterface[];
        terms: InfoInterface[];
      }> => {
        if (config.WARRANTIES.WARRANTY_TC_FROM_FIRESTORE) {
          const template = await BL_Warranty.getWarrantyTermsAndInfo(
            product?.defaultTemplate ?? ''
          );
          if (template) {
            return {
              info: (template.info as InfoInterface[]) || [],
              terms: (template.terms as InfoInterface[]) || [],
            };
          }
        }
        return {
          info:
            (tenantDefaultWarrantyContent?.warranty_info as InfoInterface[]) ||
            [],
          terms:
            (tenantDefaultWarrantyContent?.warranty_terms as InfoInterface[]) ||
            [],
        };
      };

      const { info, terms } = await resolveWarrantyContent({
        source:
          config.WARRANTIES.WARRANTY_TC_CONTENT_SOURCE ||
          Config_WarrantyContentSource.TEMPLATE,
        mergeStrategy:
          config.WARRANTIES.MERGE_STRATEGY ||
          Config_WarrantyMergeStrategy.APPEND,
        vendableInfoOverride,
        vendableTermsOverride,
        fetchTemplateContent,
      });

      setWarrantyInfo(Array.isArray(info) ? info : []);
      setWarrantyTerms(Array.isArray(terms) ? terms : []);
    };
    void getWarranty();
  }, [t('tenantLink'), product?.defaultTemplate, product?.brand, product]);

  useEffect(() => {
    if (showWarrantyDetails && warrantyDetailsRef.current) {
      warrantyDetailsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [showWarrantyDetails]);

  return (
    <div className="mt-6 px-4 sm:px-6 lg:px-0">
      {!onChangeStep && !landing && (
        <ChevronAndTitle
          title={
            searchParams.get('holdingId')
              ? t('Complete registration')
              : t('Product information')
          }
          onClick={() => navigate(-1)}
        />
      )}
      <div
        className={`mt-10 grid grid-cols-1 gap-8 md:mt-16 md:grid-cols-2 md:gap-10 lg:gap-12`}
      >
        <div
          className={`relative flex w-full flex-col items-center justify-center rounded-[6px] bg-white md:max-h-[620px] ${
            onChangeStep ? '' : 'md:shadow-[4px_6px_24px_rgba(0,0,0,0.08);]'
          } lg:w-[480px]`}
        >
          {loading && <LoadingSpinner size="sm" overlay={true} />}
          <div className="flex w-full flex-shrink-0 justify-center pt-8 md:px-8">
            <img
              src={tenantBrandLogo}
              alt={tenantBrandName}
              className="flex max-h-[64px] justify-start object-contain"
            />
          </div>

          <div className="flex min-h-0 w-full flex-grow items-center justify-center px-2 sm:px-4 md:px-8">
            <img
              src={loading ? '' : product?.img?.[0] ?? productPlaceHolder}
              className="h-[260px] w-full max-w-[520px] object-contain sm:h-[320px] md:h-[380px] md:max-h-full"
            />
          </div>

          {landing && (
            <div className="font-nunito px-6 py-8 text-center text-lg sm:px-10 md:px-16">
              <strong>{tenantInfo?.name}</strong> has partnered with{' '}
              <strong className="text-primary-900">iWarranty</strong> to help
              you manage all your products and warranties in one place.
            </div>
          )}

          {!landing && (
            <div className="flex w-full flex-shrink-0 flex-col items-center gap-6 px-4 py-4 pb-10 sm:px-6 md:px-8 md:pb-12">
              <span className="font-nunito text-center text-2xl font-bold capitalize text-gray-500">
                {product?.title ?? ''}
              </span>
              <div className="flex flex-col items-center">
                <Button
                  className="w-full max-w-[320px] px-8 md:w-fit"
                  children={
                    <>
                      {params.holdingId
                        ? t('View product')
                        : searchParams.get('holdingId')
                        ? t('Complete registration')
                        : !onChangeStep
                        ? t(`Register product`)
                        : t('Initiate claim')}
                      <ArrowRightIcon className="ml-2 h-4 w-4" />
                    </>
                  }
                  kind={'primary'}
                  onClick={() => {
                    if (onChangeStep) {
                      window.localStorage.setItem(
                        'NLIC_VendableId',
                        product?.docid ?? ''
                      );
                      window.localStorage.setItem(
                        'NLIC_VendableName',
                        product?.title ?? ''
                      );
                      onChangeStep(4);
                    } else {
                      searchParams.get('holdingId')
                        ? navigate(
                            ROUTE_NAME.PRODUCTS_PREVIEW +
                              '/' +
                              product?.docid +
                              '/register?holdingId=' +
                              searchParams.get('holdingId')
                          )
                        : params.holdingId
                        ? navigate(
                            ROUTE_NAME.PRODUCTS +
                              '/' +
                              (isSRVHolding
                                ? params.holdingId.replace('_SRV', '')
                                : params.holdingId) +
                              '/details'
                          )
                        : navigate(
                            ROUTE_NAME.PRODUCTS_PREVIEW +
                              '/' +
                              product?.docid +
                              '/register'
                          );
                    }
                  }}
                />
                {!onChangeStep && (
                  <div className="mt-5 flex w-full justify-center md:hidden">
                    <Button
                      children={t('general.warrantyInformation')}
                      kind={'outline'}
                      onClick={() => setShowWarrantyDetails(true)}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {!onChangeStep && !landing && (
          <div
            ref={warrantyDetailsRef}
            className={classNames(
              `w-full rounded-[6px] bg-white md:block md:max-h-[620px] md:basis-1/2 md:shadow-[4px_6px_24px_rgba(0,0,0,0.08);]`,
              showWarrantyDetails ? 'mt-4 block md:mt-0' : 'hidden md:block'
            )}
          >
            <div className="overflow-hidden">
              <div className="">
                <div className="hidden text-center md:block">
                  <div className="font-caudex h-[64px] border-b border-gray-200 py-4 text-xl font-bold md:text-2xl">
                    {warrantyInfo?.length > 0 && (
                      <>
                        {tenantInfo?.name === 'NuCover'
                          ? 'Care Plan Summary'
                          : t('general.warrantyInformation')}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col overflow-y-auto py-6 px-0 md:max-h-[calc(620px-64px)] md:px-8">
                {warrantyInfo &&
                  warrantyInfo.map((data: InfoInterface, index: number) => {
                    return <WarrantyInfo key={index} data={data} />;
                  })}
                {warrantyTerms &&
                  warrantyTerms.map((data: InfoInterface, index: number) => {
                    return <WarrantyInfo key={index} data={data} />;
                  })}
              </div>
            </div>
          </div>
        )}
        {landing && (
          <div
            ref={warrantyDetailsRef}
            className={classNames(
              `w-full rounded-[6px] p-6 md:max-h-[620px] md:basis-1/2 md:bg-white md:p-12 md:shadow-[4px_6px_24px_rgba(0,0,0,0.08);]`,
              showWarrantyDetails ? 'mt-4 block md:mt-0' : 'hidden md:block'
            )}
          >
            <AuthStart
              productTitle={
                product?.title ? product?.title : isSRVHolding ? ' ' : undefined
              }
              onChange={() =>
                navigate(
                  `/products/${
                    isSRVHolding
                      ? params.holdingId?.replace('_SRV', '')
                      : params.holdingId
                  }/details`
                )
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
export default VendableInfoContainer;
